import type { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { CSSProperties, ReactNode } from "react";

interface ListItemPropsCommon {
  text: ReactNode;
  itemStyle?: "medium" | "large";
  style?: CSSProperties;
}

interface ListItemPropsIcon extends ListItemPropsCommon {
  iconSrc?: never;
  icon: IconDefinition;
  subtext?: string;
  iconScaling?: 'width' | 'height';
}

interface ListItemPropsIconSrc extends ListItemPropsCommon {
  icon?: never;
  iconSrc: string;
  subtext?: string;
  iconScaling?: 'width' | 'height';
}

export type ListItemProps = ListItemPropsIcon | ListItemPropsIconSrc;

export function ListItem({ text, style, ...iconProps }: ListItemProps) {
  const { subtext } = iconProps; 
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: "4vw",
        fontSize: "23px",
        ...style,
      }}
    >
      <IconSquare {...iconProps} />
      <div
        style={{
          lineHeight: "1.4em",
        }}
        className='font-semibold text-blue-dark'
      >
        {text}
        {subtext && <p className='text-lg font-bold mt-1 w-full' style={{ opacity: 0.54, letterSpacing: '-0.03em', whiteSpace: 'normal' }}>{subtext}</p>}
      </div>
    </div>
  );
}

function IconSquare({
  icon,
  iconSrc,
  itemStyle = "medium",
  subtext = "",
  iconScaling = 'width',
}: Omit<ListItemProps, "text">) {
  const backgroundColor = subtext === "" ? "#dff3ff" : "#FFD06C";

  const imageStyle: CSSProperties = {
    display: "block",
    padding: itemStyle === "medium" ? "0.4em" : undefined,
    aspectRatio: "1/1",
    minWidth: "0",
    minHeight: "0",
    objectFit: "contain",
    marginTop: "auto",
    marginLeft: "auto",
    marginRight: "auto",
  };
  
  if (iconScaling === 'width') {
    imageStyle.width = "100%";
  } else {
    imageStyle.height = "100%";
  }

  return (
    <div
      style={{
        display: "grid",
        width: itemStyle === "medium" ? "2.5em" : "4em",
        aspectRatio: "1/1",
        position: "relative",
        borderRadius: itemStyle === "medium" ? "0.2em" : undefined,
        color: "#fff",
        alignContent: "center",
        marginTop: "4px",
      }}
    >
      {/* Background with opacity */}
      <div 
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: itemStyle === "medium" ? backgroundColor : undefined,
          opacity: subtext === "" ? 1 : 0.25,
          borderRadius: itemStyle === "medium" ? "0.2em" : undefined,
          zIndex: 1,
        }}
      />
      
      {/* Content without opacity */}
      <div style={{ position: "relative", zIndex: 2 }}>
        {icon ? (
          <FontAwesomeIcon
            style={{ display: "block", margin: "0 auto" }}
            icon={icon}
          />
        ) : iconSrc ? (
          <img
            style={imageStyle}
            src={iconSrc}
            alt=""
            aria-hidden
          />
        ) : null}
      </div>
    </div>
  );
}